import { useQuery, useMutation, useQueryClient } from "react-query";
import CategoriesService from "@/services/categories.service";
import { toast } from "react-toastify";
import { isValidURL } from "@/utils/validation";
import FlashcardService from "@/services/flashcard.service";

export const useCategories = (page: number, searchQuery: string) => {
    return useQuery({
        queryKey: ["categories", page, searchQuery],
        queryFn: () => CategoriesService.getCategories({ page, limit: 10, search: searchQuery }).then(async (res) => {
            const categoryDatas = res.data;
            for (const it of categoryDatas.categories) {
                if (it.image) {
                    const image = isValidURL(it.image) ? it.image : it.image ? await fetchSignedUrl(it.image) : null;
                    it.image = image;
                } else {
                    it.image = "";
                }
            }
            return categoryDatas
        }),
        keepPreviousData: true,
        staleTime: 1000 * 5,
        retry: false,
    });
};

export const fetchSignedUrl = async (fileKey: string): Promise<any> => {
    try {
        const res = await FlashcardService.getFile({ fileKey });
        if (res?.data?.status == 200) {
            return res?.data?.fileUrl;
        }
    } catch (err) {
        console.error("Error fetching signed URL:", err);
        return '';
    }
};

export const useGetOneCategory = (id: string) => {
    return useQuery({
        queryKey: ["category", id],
        queryFn: () => CategoriesService.getOneCategory(id).then(res => res.data),
        enabled: !!id, // chỉ fetch khi có id
        retry: 1, // có thể bỏ retry nếu muốn
    });
};

export const useCreateCategory = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: { title: string }) => CategoriesService.createCategory(data),
        onSuccess: () => {
            toast.success("Category created successfully!");
            queryClient.invalidateQueries({ queryKey: ["categories"] });
        },
        onError: (error: any) => {
            toast.error(error?.message || "Failed to create category");
        },
    });
};

export const useUpdateCategory = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: { title: string } }) =>
            CategoriesService.updateCategory(id, data),
        onSuccess: () => {
            toast.success("Category updated successfully!");
            queryClient.invalidateQueries(["categories"]);
        },
        onError: (error: any) => {
            toast.error(error?.message || "Failed to update category");
        },
    });
};

export const useDeleteCategory = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => CategoriesService.deleteCategory(id),

        onSuccess: () => {
            toast.success("Category deleted successfully.");
            queryClient.invalidateQueries({ queryKey: ["categories"] });
        },

        onError: () => {
            toast.error("Failed to delete category. Please try again.");
        },
    });
};